/* eslint-disable no-cond-assign */

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

// Components
import { HypotheticalReturnLegendCell } from '@/views/components/cells';

// Types
import LineSeriesDataPoint from '@/types/line-series-data-point';
import ChartItem from '@/types/chart-item';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LineSeriesData from '@/types/line-series-data';
import HypotheticalReturnLegendValue from '@/types/hypothetical-return-legend-value';
import RollingIntervals from '@/types/rolling-intervals';
import ComparisonItem from '@/types/comparison-item';

// Utils
import { mergeChartItems, getRollingIntervalFromDates, getGraphStartDate, getGraphData } from '@/utils/charts';
import { formatLocalizedString, formatMessageWithLocale } from '@/utils/i18n';
import { ROLLING_INTERVALS } from '@/utils/portfolios';
import { PERIODS_VALUE_CI, PERIODS_VALUE_INC } from '@/utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import ProposalToolbarIndexedOption from '@/types/proposal-toolbar-indexed-option';
import { createLineSeriesEntry, generateTargetRateSeries, getLongestGraphSerie } from '@/utils/hypothetical-return';

export default class PlanningAnnualizedRoiDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new PlanningAnnualizedRoiDAO(proposal, proposalElement);
  }

  series: LineSeriesDataPoint[][];

  formattedChartItems: ChartItem[];

  toolbarOption: ProposalToolbarOption | undefined;

  interval: RollingIntervals;

  colorOverride: string | null = null;

  out: LineSeriesData[] = [];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);
    this.series = [];
    this.formattedChartItems = [];
    this.toolbarOption = {};
    this.interval = ROLLING_INTERVALS.WEEKLY;
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const daoConfig: ProposalNS.HypotheticalReturnConfig = this.proposalElement.DAO as ProposalNS.HypotheticalReturnConfig;
    this.colorOverride = daoConfig.colorOverride !== undefined ? daoConfig.colorOverride : null;

    this.toolbarOption = this.getToolbarOption();
    if (this.toolbarOption !== undefined) {
      const { period, startDate, endDate, useMonthly, rebalancePeriod, monthsRange } = this.toolbarOption;
      let { capital, targetROI } = this.toolbarOption;

      const sectionIndex = this.getSectionIteratorIndex()?.toString();
      const indexedOptions = this.getToolbarOption()?.indexedOptions;
      if (sectionIndex && indexedOptions && indexedOptions[sectionIndex]) {
        capital = indexedOptions[sectionIndex].capital ?? capital;
        targetROI = indexedOptions[sectionIndex].targetROI ?? targetROI;
      }

      const { shortHistory } = this.proposal;

      if (capital != null && endDate && rebalancePeriod) {
        const itemDAOs: ComparisonItemDAO[] = this.getItemDAOs();
        const items = itemDAOs.map(i => i.item);
        const chartItems = mergeChartItems(items, []);
        const earliestStartDate = startDate !== null ? getGraphStartDate(chartItems, startDate) : null;
        const showSinceInceptionDisclaimer = monthsRange === PERIODS_VALUE_INC && shortHistory;
        const showMaxCommonDisclaimer = monthsRange === PERIODS_VALUE_CI && !shortHistory;
        let disclaimerKeys: string[] = [];

        this.interval = getRollingIntervalFromDates(earliestStartDate, endDate, items, useMonthly, shortHistory);
        this.formattedChartItems = getGraphData(chartItems, earliestStartDate, endDate, this.interval, rebalancePeriod, shortHistory);

        for (const item of this.formattedChartItems) {
          const dpResponse: DataPointResponse<LineSeriesDataPoint[]> = DataPoints.getHypotheticalReturnSeries(
            item,
            this.interval,
            capital,
            this.proposal.showBaseCurrencies === true,
          );
          this.series.push(dpResponse.response);
          if (dpResponse.disclaimerKeys.length > 0) {
            disclaimerKeys = [
              ...dpResponse.disclaimerKeys,
              ...(showSinceInceptionDisclaimer ? [PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE] : []),
              ...(showMaxCommonDisclaimer ? [PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE] : []),
            ];
          }
        }

        for (const disclaimerKey of disclaimerKeys) {
          this.addDisclaimerConfigs([
            {
              key: disclaimerKey,
              config: { series: this.series, interval: this.interval },
              items: itemDAOs.map(itemDAO => itemDAO.item),
            },
          ]);
        }
      }

      let i: number = 0;
      let baseDAO: ComparisonItemDAO | null = null;
      let itemDAO = null;
      // Note: Currently this will only ever have one iteration as it is used for one portfolio item in a comparison with a plan account
      // In future, if we chose to have this support multiple comparison items or the holdings of a comparison item, we may want to support multiple iterations
      while ((itemDAO = this.nextItem()) !== null) {
        if (baseDAO === null) {
          baseDAO = itemDAO;
        }

        const entry = createLineSeriesEntry(itemDAO, daoConfig, this.proposal, this.series, this.locale, this.colorOverride, i);
        this.out.push(entry);

        if (this.series[i].length) {
          const percentGrowth = period && itemDAO.item.performance.percentGrowth && itemDAO.item.performance.percentGrowth[period];
          const formattedPercentGrowth = percentGrowth ? this.formatPercentage(percentGrowth, 2) : formatMessageWithLocale('Common.NotApplicable', this.locale);
          const value: HypotheticalReturnLegendValue = {
            name: `${formatLocalizedString(itemDAO.item.name)} (${formattedPercentGrowth})`,
            size: this.series[i][this.series[i].length - 1][1],
          };
          this.addItemField(baseDAO, itemDAO.item, '', value, this.toolbarOptionConfig?.decimalPlace);
        }

        i++;
      }

      const indexOptions: ProposalToolbarIndexedOption = {};
      const planning = baseDAO?.item?.planning;
      if (planning && planning.planAccount) {
        const { planAccount } = planning;
        // Index options used to update the toolbar settings for this portfolio are set to the capital and targetROI values of the plan account
        // Index here refers to the index of the portfolio in the proposal
        indexOptions.capital = planAccount.totalValue;
        indexOptions.targetROI = planAccount.targetRateOfReturn;

        if (this.series && capital && targetROI) {
          const entry: LineSeriesData = generateTargetRateSeries(capital, targetROI, this.series, this.interval, this.locale);
          this.out.push(entry);

          if (baseDAO !== null && entry.data.length) {
            const value: HypotheticalReturnLegendValue = {
              name: `${planAccount.name} (${this.formatPercentage(planAccount.targetRateOfReturn, 2)})`,
              size: entry.data[entry.data.length - 1][1],
            };
            const field: ProposalFieldDAO = new ProposalFieldDAO('', value, ProposalStyleClassNames.ProposalTableContent);
            field.cellType = HypotheticalReturnLegendCell;
            this.addFieldDAO(baseDAO, field);
          }
        }
      }

      if (this.getSectionIteratorIndex != null && startDate == null) {
        const longestSerie = getLongestGraphSerie(this.series);
        if (longestSerie != null && longestSerie.length > 0) {
          indexOptions.startDate = new Date(longestSerie[0][0]);
        }
      }
      this.setIndexedToolbarOption(indexOptions);
    }
  }

  getLineSeries(): LineSeriesData[] {
    return this.out;
  }

  // private methods
  private addItemField(
    itemDAO: ComparisonItemDAO,
    item: ComparisonItem,
    name: string,
    value: HypotheticalReturnLegendValue,
    decimalPlace?: number,
  ): HypotheticalReturnLegendValue | undefined {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = HypotheticalReturnLegendCell;
    field.color = item.color;
    field.itemType = item.type;
    field.decimalPlace = decimalPlace;
    this.addFieldDAO(itemDAO, field);
    return value;
  }
}
